import {
  faCircleCheck,
  faSpinnerThird,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "urql";

import { Icon, Typography } from "@/components/ui-v2";
import { useUrqlResponseHandler } from "@/hooks";
import { uploadFileToUrl } from "@/utils/upload";

import { UploadFile } from "../queries.graphql";
import { UploadQueueItem, useDriveUploadQueueStore } from "./state";

type DriveUploadQueueItemProps = {
  item: UploadQueueItem;
  workspaceId: string;
  directory: string;
  canUpload: boolean;
};

export const DriveUploadQueueItem = (props: DriveUploadQueueItemProps) => {
  const { item, workspaceId, directory, canUpload } = props;
  const isRunningUploadRef = useRef(item.status !== "pending");
  const { getResponseFromState } = useUrqlResponseHandler();
  const [, uploadFile] = useMutation(UploadFile);

  const { updateItemInQueue } = useDriveUploadQueueStore();

  const runMultiPartUpload = useCallback(async () => {
    const itemFile = item.file;
    if (isRunningUploadRef.current || !itemFile) {
      return;
    }
    isRunningUploadRef.current = true;
    updateItemInQueue(item.key, { status: "uploading" });
    const uploadFileResult = await uploadFile({
      uploadFileInput: {
        workspaceId,
        directory,
        name: itemFile.name,
        contentType: itemFile.type,
        sizeBytes: itemFile.size,
      },
    });
    const {
      data: { uploadFile: file },
    } = getResponseFromState({
      state: uploadFileResult,
      onError: () => updateItemInQueue(item.key, { status: "failed_upload" }),
    });
    if (file && file.uploadUrl) {
      try {
        await uploadFileToUrl({
          signedUrl: file.uploadUrl,
          contentType: file.contentType,
          blob: itemFile,
          onProgress: ({ percent }) => {
            updateItemInQueue(item.key, {
              status: "uploading",
              text: `${itemFile.name} (${percent}%)`,
            });
          },
        });
      } catch {
        updateItemInQueue(item.key, { status: "failed_upload" });
        return;
      }
      updateItemInQueue(item.key, {
        status: "uploaded",
        text: itemFile.name,
      });
    }
  }, [
    item.key,
    item.file,
    workspaceId,
    directory,
    updateItemInQueue,
    uploadFile,
    getResponseFromState,
  ]);

  useEffect(() => {
    // trigger upload if can upload is set
    if (canUpload) {
      runMultiPartUpload();
    }
  }, [canUpload, runMultiPartUpload]);

  return <DriveUploadQueueItemRow item={item} />;
};

type DriveUploadQueueItemRowProps = {
  item: UploadQueueItem;
};

const DriveUploadQueueItemRow = (props: DriveUploadQueueItemRowProps) => {
  const { item } = props;
  let color = "secondary.3";
  let statusIcon: JSX.Element | null = null;
  switch (item.status) {
    case "pending":
      statusIcon = <Icon icon={faSpinnerThird} spin />;
      break;
    case "uploading":
      color = "primary.main";
      statusIcon = <Icon icon={faSpinnerThird} spin />;
      break;
    case "uploaded":
      color = "success.main";
      statusIcon = <Icon icon={faCircleCheck} />;
      break;
    case "failed_upload":
      color = "error.main";
      statusIcon = <Icon icon={faTriangleExclamation} />;
      break;
  }
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ px: 2, py: 2 }}
    >
      <Typography variant="small" noWrap title={item.text}>
        {item.text}
      </Typography>
      <Box sx={{ color }}>{statusIcon}</Box>
    </Stack>
  );
};
